
import React, { createRef, useEffect } from 'react';

import { Dialog, DialogContent, DialogTitle, Grid, Paper, Stack, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles';
import { unwrapResult } from '@reduxjs/toolkit';
import { connect } from 'react-redux'
import { MiniGroup, Sheet, Creature } from '../../redux/entities'
import { getGroupsOnSheet, getSheetById } from '../../redux/store'

import CreatureList from '../creatures/CreatureList';
import { useTheme } from '@mui/material/styles';
import PackedSheetView from './PackedSheetView';
import { CreatureDialog } from '../creatures/CreatureDialog';
import { FeedbackCard } from '../feedback/FeedbackCard';
import { Box } from '@mui/system';
import { DEFAULT_PAPER } from './paperSizes';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { print } from './printSheet';
import { useCreatureDrop } from './useCreatureDrop';
import { SheetSettingsPanel } from './SheetSettingsPanel';

const defaultPaper = DEFAULT_PAPER;

const useStyles = makeStyles((theme) => ({
    root: {
        '& > *': {
            margin: theme.spacing(1),
        },
    },
    extendedIcon: {
        marginRight: theme.spacing(1),
    },
    box: {
        border: "1px black",
        borderStyle: "dashed"
    },
    hidden: {
        display: "none"
    },
    sheet: {
        position: "relative"
    }
}));


function SheetView(props) {
    const classes = useStyles();
    const theme = useTheme();
    const printableRef = createRef();
    const [focusedCreature, setFocusedCreature] = React.useState(null);
    const [sheetFits, setSheetFits] = React.useState(true);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);

    const { sheet, update, remove, removeMiniGroup, updateMiniGroup, createMiniGroup, groups, alert, onDelete, createCreature, updateCreature, removeCreature } = props;
    const [proxy, setProxy] = React.useState({ ...sheet });

    function updateProxy(props) {
        console.log("proxyupdate", proxy, props)
        if (props) setProxy({ ...proxy, ...props, dirty: true });
        else setProxy(null);
    }

    useEffect(() => {
        setProxy(sheet);
    }, [sheet])

    useEffect(() => {
        function handleKeyDown(e) {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'p') {
                e.preventDefault();
                onPrint();
            }
        }
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    });

    function selectCreature(creature) {
        setFocusedCreature(creature);
    }

    const addNewCreatureFromDropEvent = useCreatureDrop({ createCreature, alert, onCreatureAdded: selectCreature });

    if (!sheet) return <p>This sheet couldn't be found.</p>

    function onSubmit() {
        console.log("Updating...");

        if (proxy.dirty) {
            delete proxy.dirty;
            update(proxy).then(unwrapResult).then(alert("Sheet saved")).catch(e => { proxy.dirty = true; });
        }
    }
    function onPrint() {
        console.log("Printing...");
        if (!sheetFits) {
            alert("Some minis don't fit on the page — resize or remove a few, then try again");
            return;
        }
        print(printableRef.current, proxy.papersize ? proxy.papersize : defaultPaper)
            .then(() => alert("Sent to printer"))
            .catch(err => alert(err.message || "Couldn't print this sheet — try again"));
    }
    function doDelete() {
        groups.forEach(group => {
            removeMiniGroup(group);
        });
        remove(sheet).then(unwrapResult).then(alert("Sheet deleted"));
        onDelete();
    }

    function onDeleteCreature(creature) {
        removeCreature(creature).then(result => { alert("Removed " + creature.name + " from your library") });
    }

    function onUpdateCreature(creature) {
        updateCreature(creature).then(result => {
            alert("Updated " + creature.name)
            // Check if the changed creature is also on the sheet
            // If so, override the minis on the sheet with new creature defaults
            const affectedGroups = groups.filter(g => (g.creature.id === creature.id));
            if (affectedGroups.length > 0) {
                alert("Library updated — minis on your sheets use the new settings")
                for (let group of affectedGroups) {
                    // override the creature in the group
                    // by NOT overriding the 'size' in the group, any creature that is explicitly
                    // resized will be
                    const updated = { ...group, creature: creature }
                    updateMiniGroup(updated)
                }
            }

        })
    }

    function onCanvasDrop(e) {
        e.preventDefault();
        console.log("Dropped", e.dataTransfer.types);
        if (e.dataTransfer.types.includes("creature")) {
            // create new mini from this creature
            const creature = JSON.parse(e.dataTransfer.getData("creature"));

            createMiniGroup({ x: 0, y: 0, sheetId: sheet.id, count: 1, creatureId: creature.id, creature, vertical: true });
        } else if (e.dataTransfer.types.includes("text/html") || (e.dataTransfer.files && e.dataTransfer.files.length > 0)) {
            addNewCreatureFromDropEvent(e).then(creature => {
                if (!creature) return;
                createMiniGroup({ x: 0, y: 0, sheetId: sheet.id, count: 1, creatureId: creature.id, creature, vertical: true });
                alert("Added to your library and this sheet at medium size");
            });
        }
    }

    return (
        <div className={classes.sheet}>


            <Grid container columns={12} spacing={2} columnSpacing={{ xs: 1, sm: 2, md: 3 }} justifyContent="space-evenly" direction="row" >

                <Grid item sm={12} md={3} > { /* Support panel */}
                    <Paper elevation={3} sx={{ padding: "1em", width: "100%", height: "100%", background: theme.supportPanel.background }}>
                        <Stack
                            direction="column"
                            justifyContent="flex-start"
                            alignItems="stretch"
                            spacing={2}
                        >
                            <Typography variant="h5">Library</Typography>
                            <Box sx={{ maxHeight: "60vh", overflow: "auto" }}>
                                <CreatureList handleImageDrop={addNewCreatureFromDropEvent} onClick={selectCreature} />
                            </Box>
                            <Box sx={{ position: "sticky" }}>
                                <FeedbackCard />
                            </Box>
                        </Stack>
                    </Paper>
                </Grid>
                <Grid item sm={12} md={9}> { /* Focus container */}
                    <Grid container alignItems="stretch" >
                        <Paper elevation={3} sx={{ padding: "1em", width: "100%", height: "100%" }}>
                            <Grid container columns={12} spacing={2}>

                                <Grid item xs={12} justifyContent="flex-start">
                                    <SheetSettingsPanel
                                        proxy={proxy}
                                        onNameChange={name => updateProxy({ name })}
                                        onNameBlur={onSubmit}
                                        onPaperChange={papersize => updateProxy({ papersize })}
                                        onDelete={() => setConfirmDeleteOpen(true)}
                                        onPrint={onPrint}
                                        sheetFits={sheetFits}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <PackedSheetView groups={groups} onCanvasDrop={onCanvasDrop} showGui={true} paper={proxy.papersize ? proxy.papersize : defaultPaper} onFitChange={setSheetFits} ref={printableRef} />
                                </Grid>


                            </Grid>

                        </Paper>
                    </Grid>
                </Grid>

                <CreatureDialog open={focusedCreature != null} creature={focusedCreature}
                    onClose={e => { selectCreature(null) }}
                    onChange={creature => { onUpdateCreature(creature) }}
                    onDelete={creature => onDeleteCreature(creature)}
                />

                <ConfirmDialog
                    open={confirmDeleteOpen}
                    title={`Delete ${sheet.name}?`}
                    description="This can't be undone."
                    confirmLabel="Delete"
                    onCancel={() => setConfirmDeleteOpen(false)}
                    onConfirm={() => { setConfirmDeleteOpen(false); doDelete(); }}
                />

            </Grid>
        </div>)

}

export function SheetViewDialog(props) {

    const { sheet, onClose, open } = props;

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>Sheet information</DialogTitle>
            <DialogContent>
                <SheetView sheet={sheet} />
            </DialogContent>
        </Dialog>
    )
}


function mapStateToProps(state, ownProps) {
    var sheet = ownProps.sheet;
    var groups = null;
    if (!sheet && ownProps.sheetId) {
        sheet = getSheetById(state, ownProps.sheetId)
    }
    if (sheet) groups = getGroupsOnSheet(state, sheet.id);
    return { sheet, groups };
};


export default connect(mapStateToProps, {
    update: Sheet.actions.updateEntityAsync,
    remove: Sheet.actions.removeEntityAsync,
    createCreature: Creature.actions.createEntityAsync,
    updateCreature: Creature.actions.updateEntityAsync,
    removeCreature: Creature.actions.removeEntityAsync,
    createMiniGroup: MiniGroup.actions.createEntityAsync,
    updateMiniGroup: MiniGroup.actions.updateEntityAsync,
    removeMiniGroup: MiniGroup.actions.removeEntityAsync,
})(SheetView)
